import { Button } from '@aecom-ai-engineering/react-components';
import { Dialog } from '@ark-ui/react/dialog';
import { Portal } from '@ark-ui/react/portal';
import { Sparkle, X } from 'lucide-react';
import { type CSSProperties, type FC, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';

import { AgentLogoMarquee } from '@/components/agent-logo-marquee';

import './agents-intro-dialog.css';

const ICON_STROKE_WIDTH = 1.75;

/**
 * The frame's two 400px strips, against the stylesheet's 584px default for the
 * loading state. Only the width: the fade is a length the stylesheet already
 * gets right at both widths. Hoisted so neither instance allocates a new object
 * per render.
 */
const MARQUEE_STYLE = { '--agent-logo-marquee-max-width': '400px' } as CSSProperties;

/** The three bullets, in the order drawn. */
const POINTS = ['point-specialist', 'point-documents', 'point-add'] as const;

interface AgentsIntroDialogProps {
  open: boolean;
  /** Called for every route out: the X, Escape and Get Started all mean the same thing. */
  onDismiss: () => void;
  /**
   * Where to put focus on dismissal.
   *
   * Needed because this card has no `Dialog.Trigger`: it opens itself on first
   * load, so Zag's return-focus falls back to whatever was focused beforehand,
   * which on a fresh page load is `document.body`. Dismissing by keyboard would
   * drop focus to the top of the document rather than to the page the card is
   * introducing. The page passes its heading.
   */
  finalFocusRef?: RefObject<HTMLElement | null>;
}

/*
 * Ark's Dialog rather than the design system's, on the same grounds
 * add-context-modal.tsx gives: that component is 640px with its own
 * header/footer structure, and this card is 480px whose header is a full-bleed
 * illustration band with an absolutely positioned close button. Overriding its
 * width and all three sections' padding is more fighting than composing.
 *
 * Deliberately no `Dialog.Description`: the bullets are a list, and wrapping a
 * `<ul>` in the description's `<p>` is invalid markup. The title alone names the
 * dialog, which is what `aria-labelledby` needs.
 */
export const AgentsIntroDialog: FC<AgentsIntroDialogProps> = ({
  open,
  onDismiss,
  finalFocusRef,
}) => {
  const { t } = useTranslation();

  return (
    <Dialog.Root
      closeOnEscape
      /*
       * The requirement: a backdrop click is inert. This card is an
       * introduction rather than a question, and a stray click behind it
       * would spend the one showing it ever gets — the three deliberate
       * routes out are the X, Escape and Get Started.
       */
      closeOnInteractOutside={false}
      finalFocusEl={finalFocusRef === undefined ? undefined : () => finalFocusRef.current}
      lazyMount
      onOpenChange={({ open: next }) => {
        if (!next) onDismiss();
      }}
      open={open}
      unmountOnExit
    >
      <Portal>
        <Dialog.Backdrop className="fixed inset-0 z-80 bg-[color-mix(in_srgb,var(--ds-color-raw-black)_40%,transparent)]" />
        {/*
         * Scrolls, and the card centres with `m-auto` rather than
         * `items-center`: centred flex children overflow equally in both
         * directions, so the top of a card taller than the viewport cannot be
         * scrolled to. Auto margins centre while there is room and collapse to
         * 0 when there is not, which keeps the whole card reachable.
         *
         * It matters here more than on a normal modal: the card is 484px and
         * `closeOnInteractOutside` is off, so on a viewport under ~516px — 200%
         * zoom on a 1366x768 screen — Get Started would be off-screen with only
         * Escape left.
         */}
        <Dialog.Positioner className="fixed inset-0 z-80 flex overflow-y-auto overscroll-contain p-[var(--ds-space-4)]">
          {/*
           * `ds-body-font` on the content, as add-context-modal, sources-modal
           * and agent-chat-drawer all do: #4935 sets the family once on the
           * AppShell root, and this is portaled to document.body, so it
           * inherits from `body` — Inter, kept for the legacy pages — and
           * would otherwise render the card in a different family from the
           * directory behind it.
           */}
          <Dialog.Content className="ds-body-font m-auto w-full max-w-[480px] shrink-0 overflow-clip rounded-[var(--ds-radius-16)] bg-[var(--ds-color-bg-primary)]">
            {/*
             * The illustration band. `overflow-clip` on the content is what
             * squares the strips off against the card's rounded corners, and
             * each strip's own mask fades its ends into this colour — so the
             * band cannot drift from the fade the way a hardcoded gradient
             * would. Top pans left-to-right, bottom right-to-left, as drawn.
             *
             * py 64, not 80: design tightened the band on 2026-09-17, taking the
             * card from 508 to 484.
             */}
            <div className="relative flex flex-col items-center gap-[var(--ds-space-2)] bg-[var(--ds-color-bg-brand-light)] py-[var(--ds-space-16)]">
              <AgentLogoMarquee direction="right" style={MARQUEE_STYLE} />
              <AgentLogoMarquee direction="left" style={MARQUEE_STYLE} />

              {/*
               * --ds-color-bg-brand for the border: Figma's
               * `--border-brand` resolves to #7a996b, and design-tokens 0.6.0
               * ships no `--ds-color-border-brand` — only primary, secondary
               * and negative — while that background token is the same hex.
               */}
              <Dialog.CloseTrigger
                aria-label={t('agents-directory.intro.close')}
                className="agents-intro-close absolute top-[var(--ds-space-4)] right-[var(--ds-space-4)] flex size-[var(--ds-space-8)] cursor-pointer items-center justify-center rounded-[var(--ds-radius-8)] border border-solid border-[var(--ds-color-bg-brand)] bg-[var(--ds-color-bg-primary)] text-[var(--ds-color-text-primary)]"
              >
                <X
                  aria-hidden="true"
                  className="size-[var(--ds-space-4)]"
                  strokeWidth={ICON_STROKE_WIDTH}
                />
              </Dialog.CloseTrigger>
            </div>

            {/* Even 32 all round; the top was 24 before design evened it up. */}
            <div className="flex flex-col gap-[var(--ds-space-6)] p-[var(--ds-space-8)]">
              <div className="flex flex-col gap-[var(--ds-space-4)]">
                <div className="flex items-center gap-[var(--ds-space-2)]">
                  {/*
                   * Not the design system's StatusBadge: its `positive` variant
                   * is a light-green field with green text, and the frame draws
                   * solid green with white. A "brand" variant upstream is a
                   * follow-up, not a reason to ship the wrong colours now.
                   */}
                  <span className="rounded-[var(--ds-radius-12)] bg-[var(--ds-color-bg-brand)] px-[var(--ds-space-2)] py-[var(--ds-space-0-5)] text-[length:var(--ds-typography-paragraph-13-regular-font-size)] leading-[var(--ds-typography-paragraph-13-regular-line-height)] text-[var(--ds-color-text-invert)]">
                    {t('agents-directory.intro.badge')}
                  </span>
                  <Dialog.Title className="text-[length:var(--ds-typography-heading-4-font-size)] font-[var(--ds-typography-paragraph-paragraph-medium-weight)] leading-[var(--ds-typography-heading-4-line-height)] tracking-[var(--ds-typography-heading-4-letter-spacing)] text-[var(--ds-color-text-primary)]">
                    {t('agents-directory.intro.title')}
                  </Dialog.Title>
                </div>

                {/*
                 * Rows are items-start, not the frame's items-center: every
                 * bullet is one line at 480px in English, but the second wraps
                 * in de/ca/es/fr/vi, and centring then floats the mark between
                 * the two lines instead of marking the first.
                 */}
                <ul className="flex flex-col gap-[var(--ds-space-3)]">
                  {POINTS.map((point) => (
                    <li className="flex items-start gap-[var(--ds-space-2)]" key={point}>
                      <span className="flex p-[var(--ds-space-1)] text-[var(--ds-color-bg-brand)]">
                        <Sparkle
                          aria-hidden="true"
                          className="size-[var(--ds-space-4)]"
                          strokeWidth={ICON_STROKE_WIDTH}
                        />
                      </span>
                      <span className="text-[length:var(--ds-typography-paragraph-small-font-size)] leading-[var(--ds-typography-paragraph-small-line-height)] text-[var(--ds-color-text-primary)]">
                        {t(`agents-directory.intro.${point}`)}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              {/*
               * `w-full` and the padding straight on the Button, no wrapper: it
               * renders a bare <button> and spreads className onto it, and
               * Button.css arrives in layer(components) — which every Tailwind
               * utility outranks whatever its specificity. That is the
               * arrangement index.css exists to guarantee, and the Agent Chat
               * pill's width and radius rely on the same thing.
               *
               * py 12 because the frame's CTA is 44px and no shipped size is:
               * md is 36 (py 8), lg 40 (py 10, and 24 at the sides), xl 48 with
               * 16px text. md plus this override is the only combination that
               * keeps the design's 14/20 label at the drawn height, and
               * `min-height` on md is a floor, so it does not fight back.
               * Horizontal padding is left alone — the button is full width, so
               * only the height is visible. Raised with the DS owner.
               */}
              <Button
                className="w-full py-[var(--ds-space-3)]"
                onClick={onDismiss}
                size="md"
                variant="solid"
              >
                {t('agents-directory.intro.get-started')}
              </Button>
            </div>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};
